import { Button, Modal, Input, Form, Alert, Space, Drawer } from "antd";
import React, { useState, useRef } from "react";
// import 'antd/dist/antd.css';
import "../css/wizard.css";
import Draggable from "react-draggable";
import ProbabilityTable from "./ProbabilityTable";
import { parentCombinations } from "./ProbabilityTable";
import { convertParentData, getEvidence } from "./Flow";
import { insertValues, calcBayesianStatsRecursive } from "./Maths";
import { lastDeleted } from "./Flow";
import { SettingOutlined } from "@ant-design/icons";
import { validateNode } from "./DataValidation";
import { getModal } from "./UserSettings";
// TODO: See if they want PlusIcon.svg
// import { ReactComponent as PlusIcon } from '../assets/PlusIcon.svg';

/*
The "wizard"
i.e. the settings popup for a node
*/

function useForceUpdate() {
  const [value, setValue] = useState(0); // integer state
  return () => setValue((value) => value + 1); // update state to force render
}

let idCheck = -100;
let editableData = {};
let errs = [];
function Wizard({ data, id }) {
  const forceUpdate = useForceUpdate();

  if (Object.keys(editableData).length === 0 || idCheck !== id) {
    editableData = JSON.parse(JSON.stringify(data));
    editableData["condProbVals"] = calcBayesianStatsRecursive(
      editableData.condProb
    );
    idCheck = id;
  }

  //------------------Popup Stuff and draggable------------------
  //Mostly from https://ant.design/components/modal/
  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [bounds, setBounds] = useState({
    left: 0,
    top: 0,
    bottom: 0,
    right: 0,
  });
  const draggleRef = useRef(null);
  const showModal = () => {
    setOpen(true);
  };
  const onStart = (_event, uiData) => {
    const { clientWidth, clientHeight } = window.document.documentElement;
    const targetRect = draggleRef.current?.getBoundingClientRect();
    if (!targetRect) {
      return;
    }
    setBounds({
      left: -targetRect.left + uiData.x,
      right: clientWidth - (targetRect.right - uiData.x),
      top: -targetRect.top + uiData.y,
      bottom: clientHeight - (targetRect.bottom - uiData.y),
    });
  };
  //--------------------------------------------------------------

  const handleOk = (e) => {
    errs = [];
    //Label
    editableData.label = document.getElementById("label-input").value;

    //Update the state labels
    let oldStateLabels = editableData.states.map((s) => Object.keys(s)[0]);
    let newStateLabels = getNewStateLabels();
    for (let i = 0; i < oldStateLabels.length; i++) {
      let oldKey = oldStateLabels[i];
      let newKey = newStateLabels[i];
      if (oldKey !== newKey) {
        editableData.states[i][newKey] = editableData.states[i][oldKey];
        delete editableData.states[i][oldKey];
      }
    }

    //Data Validation
    errs = validateNode(editableData);
    if (errs.length > 0) {
      forceUpdate();
      return;
    }

    //Updates the Probabilities
    editableData.condProb = insertValues(
      editableData.condProb,
      editableData.condProbVals
    );
    delete editableData.condProbVals;

    //Pushes the new data object
    data.funcs.nodeDataReplace(id, editableData);
    editableData = {};
    setOpen(false);
  };

  const handleCancel = (e) => {
    editableData = {};
    errs = [];
    setOpen(false);
  };

  //Helpful to work these out here
  let parentData = convertParentData(data.parentData);
  let parentStateCombinations = parentCombinations(parentData);

  function getNewStateLabels() {
    let maxJ = editableData.states.length;

    let idList = [];
    for (let j = 0; j < maxJ; j++) {
      idList.push(`prob-header-${j}`);
    }

    let newStateLabels = [];
    idList.forEach((id) => {
      let input = document.getElementById(id);
      if (input) {
        newStateLabels.push(input.value);
      }
    });

    return newStateLabels;
  }

  //When state is added, all the fields of editabledata are updated to reflect it
  //And the wizard is forced to rerender with the table
  function addState() {
    let highest = -1; //Highest StateX number if present (i.e. state names from autogenerated)
    editableData.states.forEach((state) => {
      if (Object.keys(state)[0].slice(0, 5) === "State") {
        let num = parseInt(Object.keys(state)[0].slice(5));
        if (num > highest) {
          highest = num;
        }
      }
    });
    let stateKey;
    if (highest === -1) {
      stateKey = "State" + (editableData.states.length + 1);
    } else {
      stateKey = "State" + (highest + 1);
    }
    let setStateKey = "S" + (editableData.setStates.length + 1);
    let state = {};
    state[stateKey] = 0;
    let setState = {};
    setState[setStateKey] = 0;

    editableData.states.push(state);
    editableData.setStates.push(setState);

    let obj = {};
    let key = "S" + (editableData.condProb.length + 1);
    obj[key] = editableData.condProb.flatMap(Object.values)[0];
    let condProb = JSON.parse(JSON.stringify(editableData.condProb));
    condProb.push(obj);

    //Inserts 0s into new row
    let valsPerRow;
    if (parentData.length > 0) {
      valsPerRow = parentData
        .map((p) => Object.values(p)[0].length)
        .reduce((a, b) => a * b);
    } else {
      valsPerRow = 1;
    }

    let values = calcBayesianStatsRecursive(editableData.condProb);
    values = values.concat(Array(valsPerRow).fill(0));

    condProb = insertValues(condProb, values);
    editableData.condProb = condProb;
    editableData.condProbVals = editableData.condProbVals.concat(
      Array(valsPerRow).fill(0)
    );

    forceUpdate();
  }

  //Currently problem with deleting the last state
  function delState(i) {
    lastDeleted = i;
    //i is the index of the state to be deleted
    if (getEvidence(editableData.setStates) === i) {
      editableData.setStates = [];
      for (let j = 0; j < editableData.states.length - 1; j++) {
        let obj = {};
        let key = "S" + (j + 1);
        obj[key] = 0;
        editableData.setStates.push(obj);
      }
    }

    editableData.states = editableData.states.filter(
      (state, index) => index !== i
    );

    let setStatesCopy = JSON.parse(JSON.stringify(editableData.setStates));
    let setStatesSliced = setStatesCopy.slice(0, -1);
    for (let j = 0; j < setStatesCopy.length; j++) {
      let key = Object.keys(setStatesCopy[j])[0];
      if (setStatesCopy[j][key] === 1) {
        if (j >= i) {
          setStatesSliced[j][Object.keys(setStatesSliced[j])[0]] = 0;
          if (j > i) {
            setStatesSliced[j - 1][Object.keys(setStatesSliced[j - 1])[0]] = 1;
          }
        }
      }
    }
    editableData.setStates = setStatesSliced;

    editableData.condProb = editableData.condProb.filter(
      (state, index) => index !== i
    );
    for (let j = 0; j < editableData.condProb.length; j++) {
      let oldKey = Object.keys(editableData.condProb[j])[0];
      let newKey = "S" + (j + 1);
      if (oldKey !== newKey) {
        delete Object.assign(editableData.condProb[j], {
          [newKey]: editableData.condProb[j][oldKey],
        })[oldKey];
      }
    }

    editableData.condProbVals.splice(i * valsPerRow, valsPerRow);

    forceUpdate();
  }

  let valsPerRow =
    calcBayesianStatsRecursive(editableData.condProb).length /
    editableData.states.length;
  function editProb(r, c, e) {
    let value = e.target.value;
    let rowNew = c;
    let colNew = valsPerRow - r - 1;
    let index = rowNew * valsPerRow + colNew;

    editableData.condProbVals[index] = value;

    forceUpdate();
  }

  function editStateName(i, e) {
    let newKey = e.target.value;
    let oldKey = Object.keys(editableData.states[i])[0];
    delete Object.assign(editableData.states[i], {
      [newKey]: editableData.states[i][oldKey],
    })[oldKey];

    forceUpdate();
  }

  const renderModal = () => {
    return (
      <>
        <Form id="edit-node-form" class="edit-node-name">
          <div class="edit-node-name">
            <label htmlFor="label-input">
              <strong>Name: </strong>{" "}
            </label>
            <Input
              type={"text"}
              id="label-input"
              defaultValue={data.label}
              class="edit-node-name-input"
            />
            <span class="edit-node-add-state">
              <Button onClick={() => addState()}>Add State</Button>
            </span>
          </div>
        </Form>

        <ProbabilityTable
          data={editableData}
          id={id}
          parentStateCombinations={parentStateCombinations}
          delState={delState}
          editProb={editProb}
          editStateName={editStateName}
        />
        <br></br>

        {errs.length > 0 && (
          <Space direction="vertical" style={{ width: "100%" }}>
            {errs.map((err, i) => {
              return <Alert key={i} message={err} type="error" showIcon />;
            })}
          </Space>
        )}
      </>
    );
  };

  function draggableSection(modal) {
    return (
      <>
        <Draggable
          disabled={disabled}
          bounds={bounds}
          onStart={(event, uiData) => onStart(event, uiData)}
          handle=".handle"
        >
          <div
            ref={draggleRef}
            style={{
              width: editableData.states.length < 5 ? "fit-content" : "100%",
            }}
          >
            {modal}
          </div>
        </Draggable>
      </>
    );
  }

  const cogIcon = () => {
    return (
      <>
        <SettingOutlined onClick={showModal} style={{ fontSize: "24px" }} />
      </>
    );
  };

  const finishButtons = () => {
    return (
      <>
        <Space>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button type="primary" onClick={handleOk}>
            OK
          </Button>
        </Space>
      </>
    );
  };

  const titleSection = () => {
    return (
      <>
        <div
          className="handle"
          style={{
            width: "100%",
            cursor: "move",
          }}
          onFocus={() => {}}
          onBlur={() => {}}
        >
          <text style={{ marginLeft: "5px" }}>{data.label}</text>
        </div>
      </>
    );
  };

  return (
    <>
      {cogIcon()}
      {open && (
        <>
          {getModal() ? (
            <Modal
              title={titleSection()}
              extra={finishButtons()}
              open={open}
              onOk={handleOk}
              onCancel={handleCancel}
              modalRender={(modal) => draggableSection(modal)}
              maskClosable={false}
              closable={false}
              width={500}
            >
              {renderModal()}
            </Modal>
          ) : (
            <Drawer
              title={titleSection()}
              extra={finishButtons()}
              open={open}
              onOk={handleOk}
              onCancel={handleCancel}
              maskClosable={false}
              closable={false}
              width={500}
            >
              {renderModal()}
            </Drawer>
          )}
        </>
      )}
    </>
  );
}

export default Wizard;
